import type {
  FleetRunner,
  FleetRunnerEditableSettings,
  FleetRunnerMachineSettings
} from './claude-fleet.types'

/**
 * Machine settings… on a laptop runner's ⋮ menu: its own per-machine settings, each as bin/runner.sh's flag sets it.
 * How it runs (runWhile) is shown with its command, never changed here: switching it installs or removes the watcher
 * that applies the change.
 */

export type FleetRunnerSettingKey = keyof FleetRunnerEditableSettings

export interface FleetRunnerSettingField {
  key: FleetRunnerSettingKey
  label: string
  description: string
  kind: 'switch' | 'number' | 'one-of'
  options?: { title: string; value: string }[]
}

export const FLEET_RUNNER_MIN_DISK_MAX = 1000

export const FLEET_RUNNER_SETTING_FIELDS: FleetRunnerSettingField[] = [
  {
    key: 'autoUpdate',
    kind: 'switch',
    label: 'Restart onto updates',
    description:
      'Its /runner window restarts itself onto a new workspace or plugins once its running jobs finish. Off, it only says an update is ready.'
  },
  {
    key: 'vpnReconnect',
    kind: 'switch',
    label: 'Reconnect the VPN',
    description:
      'When dev and staging stop answering for two minutes, the runner relaunches OpenVPN Connect, and DMs a sign-in link only if it still does not reconnect.'
  },
  {
    key: 'minDiskGb',
    kind: 'number',
    label: 'Free disk floor (GB)',
    description: `Under this much free disk the runner takes no new work; runs already going finish. 0 to ${FLEET_RUNNER_MIN_DISK_MAX}.`
  },
  {
    key: 'updatePlugins',
    kind: 'one-of',
    label: 'Plugin updates',
    description:
      'When its /runner window starts or restarts: update the @tattle plugins without asking, or ask each time.',
    options: [
      { title: 'Always', value: 'always' },
      { title: 'Ask', value: 'ask' }
    ]
  },
  {
    key: 'updateRepos',
    kind: 'one-of',
    label: 'Repo updates',
    description:
      'When its /runner window starts or restarts: fast-forward the other Tattle repos without asking, ask once a day, or never.',
    options: [
      { title: 'Always', value: 'always' },
      { title: 'Ask', value: 'ask' },
      { title: 'Never', value: 'never' }
    ]
  }
]

/** The machine's settings as its heartbeat reports them, or null from a runner too old to change them from ADX. */
export function runnerMachineSettings(
  runner: Pick<FleetRunner, 'health'>
): FleetRunnerMachineSettings | null {
  return runner.health?.machineSettings ?? null
}

const RUN_WHILE_TEXT: Record<NonNullable<FleetRunnerMachineSettings['runWhile']>, string> = {
  open: 'Only while a /runner window is open',
  always: 'Always, in the background',
  auto: 'In the /runner window while one is open, otherwise in the background'
}

/** How the machine runs and how to change it there, for the dialog's read-only line. */
export function runnerRunWhileText(settings: FleetRunnerMachineSettings): string {
  const now = settings.runWhile ? RUN_WHILE_TEXT[settings.runWhile] : 'Not reported'
  return `Runs: ${now}. Changed only on the machine, with bin/runner.sh --while open|always|auto.`
}

/** Only the settings the person changed, as the fleet's settings action takes them; empty when nothing changed. */
export function changedMachineSettings(
  current: FleetRunnerMachineSettings,
  chosen: Partial<FleetRunnerEditableSettings>
): Partial<FleetRunnerEditableSettings> {
  const changes: Partial<FleetRunnerEditableSettings> = {}
  for (const field of FLEET_RUNNER_SETTING_FIELDS) {
    const value = chosen[field.key]
    if (value !== undefined && value !== current[field.key])
      Object.assign(changes, { [field.key]: value })
  }
  return changes
}

/** Whether the floor is one the runner accepts: a whole number 0 to FLEET_RUNNER_MIN_DISK_MAX. */
export function isMinDiskValid(value: unknown): boolean {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= FLEET_RUNNER_MIN_DISK_MAX
  )
}

/** A settings command's changes as the status line says them, "Restart onto updates off, Free disk floor (GB) 25". */
export function machineSettingsWords(value: string): string {
  let changes: Record<string, unknown>
  try {
    changes = JSON.parse(value) as Record<string, unknown>
  } catch {
    return value
  }
  return Object.entries(changes)
    .map(([key, v]) => {
      const label = FLEET_RUNNER_SETTING_FIELDS.find(field => field.key === key)?.label ?? key
      return `${label} ${typeof v === 'boolean' ? (v ? 'on' : 'off') : String(v)}`
    })
    .join(', ')
}
